import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { useCustomPlanNavigation } from "@/hooks/useCustomPlanNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LayoutDashboard,
  FileText,
  TrendingUp,
  TrendingDown,
  Link as LinkIcon,
  FileSearch,
  Sliders,
  Calendar,
  Code,
  Settings,
  BookOpen,
  Tag,
  Package,
  Plus
} from "lucide-react";

interface CustomPlanMobileMenuProps {
  onOpenChange: (open: boolean) => void;
}

interface MobileNavLinkProps {
  to: string;
  title: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

function MobileNavLink({ to, title, onClick, icon }: MobileNavLinkProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center text-sm font-medium py-3 px-4 rounded-xl transition-all duration-200 border border-transparent hover:border-primary/20 hover:shadow-sm ${
          isActive 
            ? "bg-primary/10 text-primary border-primary/20" 
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`
      }
    >
      {icon && (
        <div className="mr-3 p-1.5 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
          <span className="text-muted-foreground group-hover:text-primary">{icon}</span>
        </div>
      )}
      <span className="font-medium">{title}</span>
    </NavLink>
  );
}

export function CustomPlanMobileMenu({ onOpenChange }: CustomPlanMobileMenuProps) {
  const { t } = useTranslation();
  const { isLoading, isCustomPlan, featureKeys } = useCustomPlanNavigation();

  if (isLoading) {
    return (
      <div className="flex flex-col h-full p-4 space-y-4">
        <Skeleton className="h-12 w-32" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // If not custom plan, don't render
  if (!isCustomPlan) {
    return null;
  }

  const hasFeature = (key: string) => featureKeys.includes(key);

  // Check which features are enabled
  const hasContractManagement = hasFeature('contract_management');
  const hasSalesAnalytics = hasFeature('sales_analytics');
  const hasCostOptimization = hasFeature('cost_optimization');
  const hasErpIntegration = hasFeature('erp_integration');
  const hasDocumentExtraction = hasFeature('document_extraction');
  const hasCustomFields = hasFeature('custom_fields');
  const hasDashboard = hasFeature('dashboard') || hasFeature('advanced_dashboard');
  const hasCalendar = hasFeature('calendar_view');
  const hasApiAccess = hasFeature('api_read') || hasFeature('api_write');

  const closeMenu = () => onOpenChange(false);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-2">
        <Logo isAuthenticated={true} />
      </div>

      <ScrollArea className="flex-1 overflow-auto">
        <div className="space-y-6 py-6">
          {/* Main Features Section */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-primary rounded-lg">
                <LayoutDashboard className="h-4 w-4 text-primary-foreground" />
              </div>
              <h2 className="text-lg font-semibold">
                {t('nav.yourFeatures', 'Your Features')}
              </h2>
            </div>
            
            <div className="space-y-2">
              {hasDashboard && (
                <MobileNavLink
                  to="/dashboard"
                  title={t('nav.dashboard', 'Dashboard')}
                  onClick={closeMenu}
                  icon={<LayoutDashboard className="h-4 w-4" />}
                />
              )}

              {hasContractManagement && (
                <>
                  <MobileNavLink
                    to="/contracts"
                    title={t('nav.contracts', 'Contracts')}
                    onClick={closeMenu}
                    icon={<FileText className="h-4 w-4" />}
                  />
                  <MobileNavLink
                    to="/contracts/new"
                    title={t('nav.newContract', 'New Contract')}
                    onClick={closeMenu}
                    icon={<Plus className="h-4 w-4" />}
                  />
                  <MobileNavLink
                    to="/categories"
                    title={t('nav.categories', 'Categories')}
                    onClick={closeMenu}
                    icon={<Tag className="h-4 w-4" />}
                  />
                  <MobileNavLink
                    to="/services"
                    title={t('nav.services', 'Services')}
                    onClick={closeMenu}
                    icon={<Package className="h-4 w-4" />}
                  />
                </>
              )}

              {hasSalesAnalytics && (
                <MobileNavLink
                  to="/sales-analytics"
                  title={t('nav.salesAnalytics', 'Sales Analytics')}
                  onClick={closeMenu}
                  icon={<TrendingUp className="h-4 w-4" />}
                />
              )}

              {hasCostOptimization && (
                <MobileNavLink
                  to="/cost-optimization"
                  title={t('nav.costOptimization', 'Cost Optimization')}
                  onClick={closeMenu}
                  icon={<TrendingDown className="h-4 w-4" />}
                />
              )}

              {hasErpIntegration && (
                <MobileNavLink
                  to="/settings/integrations"
                  title={t('nav.erpIntegration', 'ERP Integration')}
                  onClick={closeMenu}
                  icon={<LinkIcon className="h-4 w-4" />}
                />
              )}

              {hasDocumentExtraction && (
                <MobileNavLink
                  to="/documents/extraction"
                  title={t('nav.documentExtraction', 'Document Extraction')}
                  onClick={closeMenu}
                  icon={<FileSearch className="h-4 w-4" />}
                />
              )}

              {hasCustomFields && (
                <MobileNavLink
                  to="/settings/custom-fields"
                  title={t('nav.customFields', 'Custom Fields')}
                  onClick={closeMenu}
                  icon={<Sliders className="h-4 w-4" />}
                />
              )}

              {hasCalendar && (
                <MobileNavLink
                  to="/calendar"
                  title={t('nav.calendarView', 'Calendar')}
                  onClick={closeMenu}
                  icon={<Calendar className="h-4 w-4" />}
                />
              )}

              {hasApiAccess && (
                <MobileNavLink
                  to="/api-portal"
                  title={t('nav.apiPortal', 'API Portal')}
                  onClick={closeMenu}
                  icon={<Code className="h-4 w-4" />}
                />
              )}
            </div>
          </div>

          <div className="px-4">
            <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
          </div>

          {/* Account Section - Always visible */}
          <div className="px-4">
            <div className="mb-4 flex items-center space-x-2">
              <div className="p-1.5 bg-muted rounded-lg">
                <Settings className="h-4 w-4 text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold">
                {t('nav.account', 'Account')}
              </h2>
            </div>
            
            <div className="space-y-2">
              <MobileNavLink
                to="/settings"
                title={t('nav.settings', 'Settings')}
                onClick={closeMenu}
                icon={<Settings className="h-4 w-4" />}
              />
              <MobileNavLink
                to="/documentation"
                title={t('nav.help', 'Help & Documentation')}
                onClick={closeMenu}
                icon={<BookOpen className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
